import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useWaitlist } from "../context/WaitlistContext";

const FOREST = "#163A2A";
const MINT = "#8FC69B";
const CREAM = "#FAF7F2";
const LEAF = "#2F6B3F";
const ERROR = "#B23A1F";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistModal() {
  const { isOpen, closeModal } = useWaitlist();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";
    const hasMobile = !!form.mobile.trim();
    const hasEmail = !!form.email.trim();
    if (hasMobile && !/^[+\d][\d\s\-]{8,}$/.test(form.mobile.trim()))
      errs.mobile = "Enter a valid mobile number";
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";
    if (!hasMobile && !hasEmail)
      errs.contact = "Please add a mobile number or an email so we can reach you";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    if (errors.contact && (name === "mobile" || name === "email"))
      setErrors((p) => ({ ...p, contact: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          mobile: form.mobile.trim() || null,
          email: form.email.trim() || null,
        }),
      });
      if (!res.ok) throw new Error();
      const submittedName = form.name.trim();
      closeModal();
      document.body.style.overflow = "";
      setForm({ name: "", mobile: "", email: "" });
      setErrors({});
      navigate("/thankyou", { state: { name: submittedName } });
    } catch {
      setErrors((p) => ({ ...p, contact: "Something went wrong. Please try again." }));
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
    setTimeout(() => {
      setForm({ name: "", mobile: "", email: "" });
      setErrors({});
    }, 350);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          data-testid="waitlist-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-0 sm:px-4"
          style={{ backgroundColor: "rgba(22,58,42,0.72)", backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            data-testid="waitlist-modal"
            initial={{ opacity: 0, y: 56 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 56 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full sm:max-w-[500px] rounded-t-[2.25rem] sm:rounded-[2.25rem] overflow-hidden"
            style={{ backgroundColor: CREAM, boxShadow: "0 -12px 60px rgba(22,58,42,0.22)" }}
          >
            {/* Accent bar */}
            <div style={{ height: "3px", backgroundColor: MINT }} />

            {/* Close button */}
            <button
              onClick={handleClose}
              data-testid="waitlist-modal-close"
              aria-label="Close"
              className="absolute top-5 right-5 z-10 p-2 rounded-full transition-colors hover:bg-black/6"
              style={{ color: LEAF }}
            >
              <X size={18} strokeWidth={2.5} />
            </button>

            <div className="px-8 sm:px-10 pt-8 pb-10 sm:pt-9 sm:pb-12">
              <p
                className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3"
                style={{ color: LEAF }}
              >
                Early Access
              </p>
              <h2
                className="text-[2rem] sm:text-[2.4rem] leading-tight mb-7"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: FOREST }}
              >
                Shop your Protein.
              </h2>

              <form
                onSubmit={handleSubmit}
                noValidate
                data-testid="modal-form"
                className="flex flex-col gap-7"
              >
                <ModalField
                  label="Name" badge="required" id="m-name" name="name" type="text"
                  placeholder="What should we call you?" value={form.name}
                  onChange={handleChange} error={errors.name}
                  disclaimer="(We promise to reach out only when we launch our first batch)"
                  testId="modal-name-input"
                />
                <ModalField
                  label="Mobile" badge="optional" id="m-mobile" name="mobile" type="tel"
                  placeholder="+91 98xxx xxxxx" value={form.mobile}
                  onChange={handleChange} error={errors.mobile}
                  testId="modal-mobile-input"
                />
                <ModalField
                  label="Email" badge="optional" id="m-email" name="email" type="email"
                  placeholder="you@kitchen.com" value={form.email}
                  onChange={handleChange} error={errors.email}
                  testId="modal-email-input"
                />

                {errors.contact && (
                  <p className="-mt-3 text-xs" data-testid="modal-contact-error" style={{ color: ERROR }}>
                    {errors.contact}
                  </p>
                )}

                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="modal-submit-button"
                    className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-60"
                    style={{ backgroundColor: MINT, color: FOREST }}
                  >
                    {loading ? "Securing…" : "Secure Early Access →"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ModalField({ label, badge, id, name, type, placeholder, value, onChange, error, disclaimer, testId }) {
  return (
    <div>
      <div className="mb-3">
        <label htmlFor={id} className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#265632" }}>
          {label}
          {badge && (
            <span
              data-testid={`${testId}-badge`}
              className="text-[9px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full"
              style={
                badge === "required"
                  ? { color: FOREST, backgroundColor: MINT }
                  : { color: "rgba(22,58,42,0.90)", border: "1px solid rgba(22,58,42,0.28)" }
              }
            >
              {badge}
            </span>
          )}
        </label>
        {disclaimer && (
          <span className="block mt-1 text-[11px]" style={{ color: "#265632" }}>
            {disclaimer}
          </span>
        )}
      </div>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid={testId}
        autoComplete="off"
        className="waitlist-input w-full bg-transparent text-base pb-3 transition-all duration-200 outline-none"
        style={{
          color: error ? ERROR : FOREST,
          borderBottom: error ? `1px solid ${ERROR}` : "1px solid rgba(22,58,42,0.25)",
          fontFamily: "'Inter', sans-serif",
          caretColor: FOREST,
        }}
        onFocus={(e) => { if (!error) e.target.style.borderBottomColor = FOREST; }}
        onBlur={(e) => { if (!error) e.target.style.borderBottomColor = "rgba(22,58,42,0.25)"; }}
      />
      {error && (
        <p className="mt-1.5 text-xs" data-testid={`${testId}-error`} style={{ color: ERROR }}>
          {error}
        </p>
      )}
    </div>
  );
}
